import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function InviteAccept() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`).then((r) => setInv(r.data)).catch(() => setErr("This invitation could not be found."));
  }, [token]);

  const accept = async () => {
    if (!name.trim() || pw.length < 6) { toast.error("Enter your name and a password (6+ characters)"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/invites/${token}/accept`, { name, password: pw });
      localStorage.setItem("yl_token", r.data.token);
      toast.success("Welcome to Yanthra Labs Business360");
      window.location.href = "/";
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept the invitation"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#f2f6fb] dark:bg-[#0b1733] p-4">
      <div className="w-full max-w-md rounded-2xl border bg-card overflow-hidden shadow-xl" data-testid="invite-accept">
        <div className="brand-gradient text-white p-6"><Logo size={28} /></div>
        <div className="p-7">
          {err ? (
            <div className="text-center space-y-3">
              <p className="text-red-500 font-medium">{err}</p>
              <Link to="/login" className="text-sm text-[#29abe2]">Go to sign in</Link>
            </div>
          ) : !inv ? (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading invitation…</div>
          ) : !inv.valid ? (
            <div className="text-center space-y-3">
              <p className="text-red-500 font-medium">This invitation has expired or was already used.</p>
              <Link to="/login" className="text-sm text-[#29abe2]">Go to sign in</Link>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[#29abe2]"><ShieldCheck size={18} /><span className="text-xs font-semibold tracking-wider uppercase">You're invited</span></div>
              <h1 className="text-xl font-bold text-[#0f2557] dark:text-[#29abe2]">Join {inv.project_name || "Business360"}</h1>
              <p className="text-sm text-muted-foreground">{inv.invited_by || "An administrator"} invited <b>{inv.email}</b> to join as a <b>{inv.role}</b>. Set a password to get started.</p>
              <div className="space-y-2">
                <div><Label className="text-xs">Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="invite-name" /></div>
                <div><Label className="text-xs">Create a password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" data-testid="invite-password" /></div>
              </div>
              <Button className="w-full gap-2" onClick={accept} disabled={busy} data-testid="invite-accept-btn">
                {busy ? <Loader2 className="animate-spin" size={15} /> : <ShieldCheck size={15} />} Accept & create account
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
